import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Ship,
  Plane,
  Truck,
  Train,
  Pencil,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  X,
  Shield,
  Calculator,
} from 'lucide-react';
import type {
  TransportMode,
  FreightLegType,
} from '@/features/master-data/types/freight-route.types';
import {
  FREIGHT_LEG_TYPE_LABELS,
  TRANSPORT_MODE_LABELS,
} from '@/features/master-data/types/freight-route.types';
import type { QuoteFreightLeg, ChargeMethod, SurchargeType } from '../types/quote-freight.types';
import { CHARGE_METHOD_LABELS, SURCHARGE_TYPE_LABELS } from '../types/quote-freight.types';
import { cn } from '@/lib/utils/cn';
import type { CalculatedLeg } from '../hooks/use-quote-freight-calculation';

// --- Transport mode icon map ---
const TRANSPORT_ICONS: Record<TransportMode, React.ElementType> = {
  SEA: Ship,
  AIR: Plane,
  LAND: Truck,
  RAIL: Train,
  MULTIMODAL: Truck,
};

// --- Leg type badge color map ---
const LEG_TYPE_COLORS: Record<FreightLegType, string> = {
  INLAND_ORIGIN: 'bg-info/20 text-info border-info/30',
  INTERNATIONAL: 'bg-info/20 text-info border-info/30',
  INLAND_DESTINATION: 'bg-alerta/20 text-alerta border-alerta/30',
  HANDLING: 'bg-muted/20 text-muted-foreground border-border',
};

// --- Props ---
interface QuoteFreightLegCardProps {
  leg: CalculatedLeg;
  onUpdate: (legId: string, data: Partial<QuoteFreightLeg>) => void;
  onDelete: (legId: string) => void;
  onAddSurcharge: (
    legId: string,
    data: { concept: string; type: SurchargeType; value: number }
  ) => void;
  onDeleteSurcharge: (legId: string, surchargeId: string) => void;
  readOnly?: boolean;
}

export function QuoteFreightLegCard({
  leg,
  onUpdate,
  onDelete,
  onAddSurcharge,
  onDeleteSurcharge,
  readOnly = false,
}: QuoteFreightLegCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);

  // New surcharge inline form state
  const [newSurchargeConcept, setNewSurchargeConcept] = useState('');
  const [newSurchargeType, setNewSurchargeType] = useState<SurchargeType>('FLAT');
  const [newSurchargeValue, setNewSurchargeValue] = useState('');

  const TransportIcon = TRANSPORT_ICONS[leg.transportMode];
  const excluded = !leg.includedByIncoterm;

  // --- Helpers ---
  const fmt = (v: number) =>
    `$${v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const rateFieldForMethod = (method: ChargeMethod) => {
    switch (method) {
      case 'CBM':
        return 'ratePerCbm';
      case 'KG':
        return 'ratePerKg';
      case 'FLAT':
        return 'flatRate';
      case 'MANUAL':
        return 'manualAmount';
    }
  };

  const currentRateValue = () => {
    const field = rateFieldForMethod(leg.chargeMethod);
    return leg[field] as number;
  };

  const rateLabel = (method: ChargeMethod) => {
    switch (method) {
      case 'CBM':
        return 'Tarifa $/m³';
      case 'KG':
        return 'Tarifa $/kg';
      case 'FLAT':
        return 'Monto Fijo $';
      case 'MANUAL':
        return 'Monto Manual $';
    }
  };

  // --- Handlers ---
  const handleChargeMethodChange = (value: string) => {
    onUpdate(leg.id, { chargeMethod: value as ChargeMethod });
  };

  const handleRateChange = (value: string) => {
    const field = rateFieldForMethod(leg.chargeMethod);
    onUpdate(leg.id, { [field]: parseFloat(value) || 0 });
  };

  const handleMinimumChargeChange = (value: string) => {
    onUpdate(leg.id, { minimumCharge: value ? parseFloat(value) : null });
  };

  const handleInsuranceToggle = (checked: boolean) => {
    onUpdate(leg.id, { hasInsurance: checked });
  };

  const handleInsuranceRateChange = (value: string) => {
    onUpdate(leg.id, { insuranceRate: parseFloat(value) || 0 });
  };

  const handleAddSurcharge = () => {
    if (!newSurchargeConcept.trim() || !newSurchargeValue) return;
    onAddSurcharge(leg.id, {
      concept: newSurchargeConcept.trim(),
      type: newSurchargeType,
      value: parseFloat(newSurchargeValue) || 0,
    });
    setNewSurchargeConcept('');
    setNewSurchargeType('FLAT');
    setNewSurchargeValue('');
  };

  const hasSurcharges = leg.surcharges.length > 0;
  const hasInsurance = leg.hasInsurance && leg.insuranceAmount > 0;

  return (
    <div
      className={cn(
        'rounded-lg border bg-card text-card-foreground shadow-sm transition-all',
        excluded && 'opacity-50'
      )}
    >
      {/* --- Header --- */}
      <div className="flex w-full items-center gap-2 pr-2 hover:bg-muted/50 transition-colors">
        <button
          type="button"
          className="flex flex-1 items-center gap-3 p-3 text-left min-w-0"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}

          <TransportIcon className="h-5 w-5 shrink-0 text-muted-foreground" />

          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-medium truncate">{leg.origin}</span>
            <span className="text-muted-foreground text-xs">&rarr;</span>
            <span className="text-sm font-medium truncate">{leg.destination}</span>
          </div>

          <Badge variant="outline" className={cn('shrink-0 text-xs', LEG_TYPE_COLORS[leg.legType])}>
            {FREIGHT_LEG_TYPE_LABELS[leg.legType]}
          </Badge>

          {/* Quick info badges */}
          {hasSurcharges && (
            <Badge
              variant="outline"
              className="shrink-0 text-xs bg-alerta/10 text-alerta border-alerta/30"
            >
              +{leg.surcharges.length} recargo{leg.surcharges.length > 1 ? 's' : ''}
            </Badge>
          )}
          {hasInsurance && (
            <Badge
              variant="outline"
              className="shrink-0 text-xs bg-info/10 text-info border-info/30"
            >
              <Shield className="h-3 w-3 mr-1" />
              Seguro
            </Badge>
          )}

          <div className="flex-1" />

          {excluded && <span className="text-xs text-muted-foreground italic">Excluido</span>}

          <span
            className={cn(
              'text-sm font-semibold tabular-nums',
              excluded ? 'line-through text-muted-foreground' : 'text-ok'
            )}
          >
            {fmt(leg.calculatedAmount)}
          </span>
        </button>

        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(leg.id);
            }}
            title="Eliminar tramo"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* --- Expanded Content --- */}
      {expanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-3">
          {/* Calculation breakdown */}
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <Calculator className="h-3.5 w-3.5" />
              Desglose del cálculo
            </div>

            {/* Base amount with formula */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Base:</span>
                <span className="text-xs text-muted-foreground/70">{leg.calcDescription}</span>
              </div>
              <span className="tabular-nums font-medium">{fmt(leg.baseAmount)}</span>
            </div>

            {/* Surcharges breakdown */}
            {leg.surcharges.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm pl-3">
                <div className="flex items-center gap-2">
                  <span className="text-alerta">+</span>
                  <span className="text-muted-foreground">{s.concept}</span>
                  <span className="text-xs text-muted-foreground/70">
                    ({s.type === 'PERCENTAGE' ? `${s.value}% de base` : `fijo`})
                  </span>
                </div>
                <span className="tabular-nums text-alerta">{fmt(s.calculatedAmount)}</span>
              </div>
            ))}

            {/* Insurance */}
            {hasInsurance && (
              <div className="flex items-center justify-between text-sm pl-3">
                <div className="flex items-center gap-2">
                  <span className="text-info">+</span>
                  <Shield className="h-3 w-3 text-info" />
                  <span className="text-muted-foreground">Seguro</span>
                  <span className="text-xs text-muted-foreground/70">
                    ({leg.insuranceRate}% sobre FOB + base)
                  </span>
                </div>
                <span className="tabular-nums text-info">{fmt(leg.insuranceAmount)}</span>
              </div>
            )}

            {/* Provision */}
            {leg.provisionAmount > 0 && (
              <div className="flex items-center justify-between text-sm pl-3">
                <div className="flex items-center gap-2">
                  <span className="text-purple-400">+</span>
                  <span className="text-muted-foreground">Provisión</span>
                </div>
                <span className="tabular-nums text-purple-400">{fmt(leg.provisionAmount)}</span>
              </div>
            )}

            {/* Total line */}
            <div className="flex items-center justify-between text-sm font-semibold border-t border-border/50 pt-2 mt-1">
              <span>Total tramo</span>
              <span className={excluded ? 'text-muted-foreground line-through' : 'text-ok'}>
                {fmt(leg.calculatedAmount)}
              </span>
            </div>
          </div>

          {/* Summary info */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>{TRANSPORT_MODE_LABELS[leg.transportMode]}</span>
            <span>•</span>
            <span>{CHARGE_METHOD_LABELS[leg.chargeMethod]}</span>
            {leg.minimumCharge != null && (
              <>
                <span>•</span>
                <span>Mín. {fmt(leg.minimumCharge)}</span>
              </>
            )}
            {leg.estimatedDays != null && (
              <>
                <span>•</span>
                <span>{leg.estimatedDays} días est.</span>
              </>
            )}
            {leg.providerId && (
              <>
                <span>•</span>
                <span>Proveedor asignado</span>
              </>
            )}
          </div>

          {/* --- Edit form --- */}
          {!readOnly && editing && (
            <div className="space-y-3 border-t pt-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {/* Charge method */}
                <div className="space-y-1">
                  <Label className="text-xs">Método de cobro</Label>
                  <Select value={leg.chargeMethod} onValueChange={handleChargeMethodChange}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(CHARGE_METHOD_LABELS) as ChargeMethod[]).map((m) => (
                        <SelectItem key={m} value={m}>
                          {CHARGE_METHOD_LABELS[m]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Rate input */}
                <div className="space-y-1">
                  <Label className="text-xs">{rateLabel(leg.chargeMethod)}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-8 text-xs"
                    value={currentRateValue()}
                    onChange={(e) => handleRateChange(e.target.value)}
                  />
                </div>

                {/* Minimum charge */}
                <div className="space-y-1">
                  <Label className="text-xs">Cargo mínimo $</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-8 text-xs"
                    value={leg.minimumCharge ?? ''}
                    onChange={(e) => handleMinimumChargeChange(e.target.value)}
                    placeholder="Sin mínimo"
                  />
                </div>

                {/* Estimated days */}
                <div className="space-y-1">
                  <Label className="text-xs">Días estimados</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    className="h-8 text-xs"
                    value={leg.estimatedDays ?? ''}
                    onChange={(e) =>
                      onUpdate(leg.id, {
                        estimatedDays: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    placeholder="—"
                  />
                </div>
              </div>

              {/* Insurance row */}
              <div className="flex items-center gap-4 p-2 bg-info/5 rounded-lg border border-info/20">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={leg.hasInsurance}
                    onCheckedChange={(v) => handleInsuranceToggle(v === true)}
                  />
                  <Label className="text-xs flex items-center gap-1">
                    <Shield className="h-3 w-3 text-info" />
                    Seguro
                  </Label>
                </div>
                {leg.hasInsurance && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className="h-8 text-xs w-20"
                      value={leg.insuranceRate}
                      onChange={(e) => handleInsuranceRateChange(e.target.value)}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                    {leg.insuranceAmount > 0 && (
                      <span className="text-xs text-info ml-2">= {fmt(leg.insuranceAmount)}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Surcharges */}
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1">
                  Recargos
                  {hasSurcharges && (
                    <Badge
                      variant="outline"
                      className="text-xs ml-1 bg-alerta/10 text-alerta border-alerta/30"
                    >
                      {leg.surcharges.length}
                    </Badge>
                  )}
                </Label>

                {/* Existing surcharges */}
                {leg.surcharges.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-2 text-sm rounded px-2 py-1.5 bg-muted/30"
                  >
                    <span className="flex-1 truncate text-xs">{s.concept}</span>
                    <span className="text-muted-foreground text-xs">
                      {s.type === 'PERCENTAGE' ? `${s.value}%` : fmt(s.value)}
                    </span>
                    <span className="text-alerta text-xs font-medium tabular-nums">
                      = {fmt(s.calculatedAmount)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => onDeleteSurcharge(leg.id, s.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}

                {/* Add surcharge inline */}
                <div className="flex items-center gap-2">
                  <Input
                    className="h-8 text-xs flex-1"
                    placeholder="Concepto (ej: BAF, THC)"
                    value={newSurchargeConcept}
                    onChange={(e) => setNewSurchargeConcept(e.target.value)}
                  />
                  <Select
                    value={newSurchargeType}
                    onValueChange={(v) => setNewSurchargeType(v as SurchargeType)}
                  >
                    <SelectTrigger className="h-8 text-xs w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(SURCHARGE_TYPE_LABELS) as SurchargeType[]).map((t) => (
                        <SelectItem key={t} value={t}>
                          {SURCHARGE_TYPE_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-8 text-xs w-20"
                    placeholder="Valor"
                    value={newSurchargeValue}
                    onChange={(e) => setNewSurchargeValue(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={handleAddSurcharge}
                    disabled={!newSurchargeConcept.trim() || !newSurchargeValue}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!readOnly && (
            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setEditing(!editing)}
              >
                <Pencil className="h-3 w-3" />
                {editing ? 'Cerrar edición' : 'Editar'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
                onClick={() => onDelete(leg.id)}
              >
                <Trash2 className="h-3 w-3" />
                Eliminar
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
